import { Anchor, Badge, Breadcrumbs, Button, Code, Group, Stack, Table, Text, Title } from '@mantine/core';
import { useState, type ReactNode } from 'react';
import { MethodBadge } from './ExplorerParts';
import { compareModules } from './module-filter';
import { getModuleLabel, type ModuleId } from './module-labels';
import { isObject } from './openapi-utils';
import { getAncestors, type ModuleHighlight, type SpecNode } from './spec-tree';

const ENUM_PREVIEW_COUNT = 8;

/** Validation keywords worth surfacing, with the label shown for each */
const CONSTRAINTS: [keyword: string, label: string][] = [
  ['format', 'Format'],
  ['minimum', 'Minimum'],
  ['exclusiveMinimum', 'Exclusive minimum'],
  ['maximum', 'Maximum'],
  ['exclusiveMaximum', 'Exclusive maximum'],
  ['multipleOf', 'Multiple of'],
  ['minLength', 'Min length'],
  ['maxLength', 'Max length'],
  ['pattern', 'Pattern'],
  ['minItems', 'Min items'],
  ['maxItems', 'Max items'],
  ['uniqueItems', 'Unique items'],
  ['const', 'Constant value'],
  ['default', 'Default'],
];

const formatValue = (value: unknown) => (typeof value === 'string' ? value : JSON.stringify(value));

function ModuleBadges({ modules, selected }: { modules: Iterable<ModuleId>; selected: ReadonlySet<ModuleId> }) {
  return (
    <Group gap={6}>
      {[...modules].sort(compareModules).map((module) => (
        <Badge
          key={module}
          variant={selected.has(module) ? 'filled' : 'light'}
          color={selected.has(module) ? 'teal' : 'gray'}
          tt="none"
          radius="sm"
        >
          {getModuleLabel(module)}
        </Badge>
      ))}
    </Group>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack gap={6}>
      <Text size="xs" fw={700} tt="uppercase" c="dimmed">
        {title}
      </Text>
      {children}
    </Stack>
  );
}

function EnumValues({ values }: { values: unknown[] }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? values : values.slice(0, ENUM_PREVIEW_COUNT);

  return (
    <Section title="Values">
      <Stack gap={4}>
        {visible.map((value) => (
          <Code key={formatValue(value)} w="fit-content">
            {formatValue(value)}
          </Code>
        ))}
      </Stack>
      {values.length > ENUM_PREVIEW_COUNT && (
        <Button variant="subtle" size="compact-xs" w="fit-content" onClick={() => setShowAll((s) => !s)}>
          {showAll ? 'Show fewer values' : `Show all ${values.length} values`}
        </Button>
      )}
    </Section>
  );
}

type SpecNodeDetailsProps = {
  node: SpecNode;
  nodes: ReadonlyMap<string, SpecNode>;
  highlights: ReadonlyMap<string, ModuleHighlight> | null;
  selectedModules: ReadonlySet<ModuleId>;
  onNavigate: (value: string) => void;
};

function SpecNodeDetails({ node, nodes, highlights, selectedModules, onNavigate }: SpecNodeDetailsProps) {
  const { schema } = node;

  const ancestors = getAncestors(nodes, node.value);

  const children = node.children.map((value) => nodes.get(value)).filter((child): child is SpecNode => !!child);
  const variants = children.filter((child) => child.kind === 'variant');
  const fields = children.filter((child) => child.kind !== 'variant');

  const items = isObject(schema.items) ? schema.items : undefined;
  const enumValues = Array.isArray(schema.enum) ? schema.enum : Array.isArray(items?.enum) ? items.enum : undefined;
  const constraints = CONSTRAINTS.filter(([keyword]) => keyword in schema);
  const description = typeof schema.description === 'string' ? schema.description : undefined;
  const units = schema['x-units'];
  const nestedModules = [...node.subtreeModules].filter((m) => !node.ownModules.includes(m));
  const highlight = highlights?.get(node.value);

  return (
    <Stack gap="md">
      {ancestors.length > 0 && (
        <Breadcrumbs separatorMargin={4} styles={{ root: { flexWrap: 'wrap', rowGap: 4 } }}>
          {ancestors.map((ancestor) => (
            <Anchor key={ancestor.value} size="xs" component="button" onClick={() => onNavigate(ancestor.value)}>
              {ancestor.name}
            </Anchor>
          ))}
        </Breadcrumbs>
      )}

      {node.operation ? (
        <Stack gap="xs">
          <Group gap="xs">
            <MethodBadge method={node.operation.method} />
            <Code fz="md">{node.operation.path}</Code>
          </Group>
          {node.operation.summary && <Title order={3}>{node.operation.summary}</Title>}
          {node.operation.description && <Text>{node.operation.description}</Text>}
          {node.operation.operationId && (
            <Text size="sm" c="dimmed">
              Operation ID: <Code>{node.operation.operationId}</Code>
            </Text>
          )}
        </Stack>
      ) : (
        <Stack gap={6}>
          <Group gap="xs" align="baseline">
            <Title
              order={3}
              ff={node.kind === 'property' ? 'monospace' : undefined}
              style={{ wordBreak: 'break-word' }}
            >
              {node.name}
            </Title>
            {node.typeLabel && (
              <Text size="sm" c="dimmed" ff="monospace">
                {node.typeLabel}
              </Text>
            )}
            {node.required && (
              <Badge color="orange" variant="outline" size="sm" tt="none">
                required
              </Badge>
            )}
            {schema.deprecated === true && (
              <Badge color="red" variant="light" size="sm" tt="none">
                deprecated
              </Badge>
            )}
          </Group>
          {description && <Text style={{ whiteSpace: 'pre-line' }}>{description}</Text>}
        </Stack>
      )}

      {units !== undefined && (
        <Text size="sm">
          Units:{' '}
          <Text span fw={600}>
            {String(units)}
          </Text>
        </Text>
      )}

      {node.ownModules.length > 0 && (
        <Section title="Modules">
          <ModuleBadges modules={node.ownModules} selected={selectedModules} />
        </Section>
      )}

      {highlight === 'inherited' && (
        <Text size="sm" c="dimmed">
          Included as part of a field used by a selected module.
        </Text>
      )}

      {nestedModules.length > 0 && (
        <Section
          title={node.ownModules.length > 0 ? 'Other modules used by nested fields' : 'Modules used by nested fields'}
        >
          <ModuleBadges modules={nestedModules} selected={selectedModules} />
        </Section>
      )}

      {node.refName && (
        <Text size="sm" c="dimmed">
          Schema: <Code>{node.refName}</Code>
          {node.circular && ' (refers back to a parent schema, so its fields are not repeated here)'}
        </Text>
      )}

      {constraints.length > 0 && (
        <Section title="Constraints">
          <Table withRowBorders={false} verticalSpacing={2} horizontalSpacing={0} w="fit-content">
            <Table.Tbody>
              {constraints.map(([keyword, label]) => (
                <Table.Tr key={keyword}>
                  <Table.Td pr="md">
                    <Text size="sm" c="dimmed">
                      {label}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Code>{formatValue(schema[keyword])}</Code>
                  </Table.Td>
                </Table.Tr>
              ))}
              {schema.additionalProperties === false && (
                <Table.Tr>
                  <Table.Td pr="md">
                    <Text size="sm" c="dimmed">
                      Additional properties
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">Not allowed</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Section>
      )}

      {enumValues && <EnumValues key={node.value} values={enumValues} />}

      {variants.length > 0 && (
        <Section title="One of">
          <Stack gap={4}>
            {variants.map((variant) => (
              <Anchor
                key={variant.value}
                size="sm"
                component="button"
                ta="left"
                onClick={() => onNavigate(variant.value)}
              >
                {variant.name}
              </Anchor>
            ))}
          </Stack>
        </Section>
      )}

      {fields.length > 0 && (
        <Section title={node.kind === 'operation' ? 'Contents' : node.kind === 'responses' ? 'Status codes' : 'Fields'}>
          <Table striped highlightOnHover verticalSpacing={4} fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Modules</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {fields.map((field) => (
                <Table.Tr key={field.value} data-highlight={highlights?.get(field.value)} className="spec-field-row">
                  <Table.Td>
                    <Anchor
                      size="sm"
                      component="button"
                      ta="left"
                      ff={field.kind === 'property' ? 'monospace' : undefined}
                      onClick={() => onNavigate(field.value)}
                    >
                      {field.name}
                    </Anchor>
                    {field.required && (
                      <Text span c="orange" ml={4}>
                        *
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed" ff="monospace">
                      {field.kind === 'property' || field.kind === 'response' ? field.typeLabel : ''}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{[...field.ownModules].sort(compareModules).join(', ')}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Section>
      )}
    </Stack>
  );
}

export default SpecNodeDetails;
